import type { ReactNode } from "react";

const HOME = "https://ottercraft.dev";

/**
 * The static, crawlable half of the page: what OtterPDF does, how it works,
 * and the handful of questions that actually come up. Pure markup with no
 * runtime state, so the same component is prerendered into index.html at
 * build time (see prerender.tsx) and rendered again by the app on mount.
 *
 * Deliberately short — it sits under a tool, not on a landing page. The
 * long-form guides live on ottercraft.dev; this only needs to say what the
 * thing is. Questions are collapsed <details> rows: indexable, one line each.
 */
export function Explainer() {
  return (
    <section id="about" aria-labelledby="about-title" className="mt-16 border-t border-rule pt-12">
      <p className="eyebrow">About OtterPDF</p>
      <h2
        id="about-title"
        className="mt-2 max-w-[600px] font-serif text-[26px] font-semibold leading-[1.1] tracking-[-0.01em] text-pine sm:text-[30px]"
      >
        Fix up a PDF without uploading it.
      </h2>
      <p className="mt-3 max-w-[600px] text-[14.5px] leading-[1.6] text-soft">
        Merge a few PDFs into one, pull out a page range, put pages in a different order, turn a
        sideways scan, delete the blank pages. Normally that means uploading a contract or a bank
        statement to some website. Here the whole job happens inside this browser tab.
      </p>

      <ol className="mt-8 grid gap-6 sm:grid-cols-3">
        <Step n="01" title="Your browser reads the file">
          <a className="link" href="https://mozilla.github.io/pdf.js/" rel="noopener">pdf.js</a>{" "}
          opens the PDF in a background worker on this page and draws the thumbnails. The file
          stays on your device.
        </Step>
        <Step n="02" title="You arrange the pages">
          Reorder, rotate, delete, tick the pages you want, or type a range like{" "}
          <Code>3-7</Code>. Nothing is written until you press Download.
        </Step>
        <Step n="03" title="Your browser writes the new PDF">
          <a className="link" href="https://pdf-lib.js.org/" rel="noopener">pdf-lib</a> copies the
          pages you kept into a new document and saves it to your downloads folder.
        </Step>
      </ol>

      <p className="mt-8 max-w-[680px] text-[13.5px] leading-[1.6] text-soft">
        Pages are copied, not re-rendered, so nothing loses quality. Rotation is written into the
        file, so it stays put in every viewer. There is no account and no size limit on our side.
        After the first visit the tool also works offline.
      </p>

      <h3 id="faq" className="mt-12 scroll-mt-6 font-serif text-[20px] font-semibold leading-tight text-pine">
        Questions people ask
      </h3>
      <div className="mt-3 divide-y divide-rule border-y border-rule">
        <Faq q="How do I merge PDFs?">
          Add the files, several at once or one after another. Each file&rsquo;s pages go after the
          ones already there. Move pages with the arrows if you need to, then press{" "}
          <em>Download</em>.{" "}
          <a className="link" href={`${HOME}/guides/merge-pdf-files-in-your-browser`}>Full guide.</a>
        </Faq>
        <Faq q="How do I split a PDF or extract some pages?">
          Type a range in the <em>Split</em> card (<Code>3-7</Code>, <Code>1,4,9-11</Code>,{" "}
          <Code>20-</Code>) and press <em>Export range</em>. Or tick pages and export the selection.{" "}
          <a className="link" href={`${HOME}/guides/split-pdf-extract-pages`}>Full guide.</a>
        </Faq>
        <Faq q="How do I rotate pages permanently?">
          Press the rotate button on a page (90° per press), or tick several and rotate them
          together, then download. Unlike &ldquo;rotate view&rdquo; in a PDF viewer, this is saved
          in the file. <a className="link" href={`${HOME}/guides/rotate-pdf-pages`}>Full guide.</a>
        </Faq>
        <Faq q="Is there a file-size limit?">
          Only your device&rsquo;s memory. OtterPDF warns above 100&nbsp;MB per file, because a
          few hundred high-resolution scanned pages can use up a phone&rsquo;s memory. On a laptop,
          much bigger files are usually fine.
        </Faq>
        <Faq q="Password-protected PDFs, forms, links?">
          Password-protected files can&rsquo;t be opened — remove the password in the app that
          made the file first. Forms are fine: switch to <em>Fill &amp; sign</em> to type into the
          fields, and they stay editable in the saved file. When pages from several files are
          combined, the fields are flattened (their values are drawn onto the page) because a
          PDF form can&rsquo;t be carried between documents. Internal links and bookmarks may not
          survive a merge.
        </Faq>
      </div>

      <p className="mt-6 text-[13px] leading-[1.6] text-muted">
        OtterPDF is part of <a className="link" href={HOME}>ottercraft.dev</a>, a few small free
        tools that run in your browser.
      </p>
    </section>
  );
}

function Step({ n, title, children }: { n: string; title: string; children: ReactNode }) {
  return (
    <li className="flex flex-col gap-1">
      <span className="font-mono text-[11px] text-teal-deep">{n}</span>
      <span className="font-serif text-[16px] font-semibold leading-tight text-pine">{title}</span>
      <p className="text-[13px] leading-[1.55] text-soft">{children}</p>
    </li>
  );
}

function Faq({ q, children }: { q: string; children: ReactNode }) {
  return (
    <details className="group py-3">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-serif text-[16px] leading-tight text-pine [&::-webkit-details-marker]:hidden">
        {q}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="shrink-0 text-muted transition-transform group-open:rotate-45" aria-hidden="true">
          <path d="M12 5v14" /><path d="M5 12h14" />
        </svg>
      </summary>
      <p className="mt-2 max-w-[720px] text-[14px] leading-[1.6] text-soft">{children}</p>
    </details>
  );
}

function Code({ children }: { children: ReactNode }) {
  return <code className="rounded bg-pine/10 px-1 py-0.5 font-mono text-[12px] text-pine-deep">{children}</code>;
}
